// T047: Draw Phase Handler

import type { GameState, Card } from '../../types';

const CARDS_TO_DRAW = 2;
const COUNTDOWN_START = 5;

/**
 * Draw cards from deck to hand.
 */
function drawCards(state: GameState, playerId: string, count: number): { cards: Card[]; deck: Card[] } {
  const player = playerId === state.player1.id ? state.player1 : state.player2;
  const deck = [...player.deck];
  const drawnCards: Card[] = [];

  const actualCount = Math.min(count, deck.length);

  for (let i = 0; i < actualCount; i++) {
    const card = deck.shift();
    if (card) {
      drawnCards.push({
        ...card,
        location: 'hand',
      });
    }
  }

  return { cards: drawnCards, deck };
}

/**
 * Execute the draw phase for a player.
 * Draws 2 cards from deck to hand.
 * Triggers countdown when deck becomes empty.
 */
export function execute(state: GameState, playerId: string): GameState {
  const isPlayer1 = playerId === state.player1.id;
  const player = isPlayer1 ? state.player1 : state.player2;

  const { cards: drawnCards, deck: newDeck } = drawCards(state, playerId, CARDS_TO_DRAW);

  const newHand = [...player.hand, ...drawnCards];

  // Check if deck became empty - trigger countdown
  let countdownTurns = state.countdownTurns;
  if (newDeck.length === 0 && player.deck.length > 0) {
    countdownTurns = COUNTDOWN_START;
  }

  if (isPlayer1) {
    return {
      ...state,
      player1: {
        ...state.player1,
        deck: newDeck,
        hand: newHand,
      },
      countdownTurns,
    };
  } else {
    return {
      ...state,
      player2: {
        ...state.player2,
        deck: newDeck,
        hand: newHand,
      },
      countdownTurns,
    };
  }
}

/**
 * Check if draw phase can be executed.
 */
export function canExecute(state: GameState, playerId: string): boolean {
  const player = playerId === state.player1.id ? state.player1 : state.player2;
  return player.deck.length > 0 && state.currentPhase === 'draw';
}

/**
 * Get number of cards that will be drawn.
 */
export function getDrawCount(state: GameState, playerId: string): number {
  const player = playerId === state.player1.id ? state.player1 : state.player2;
  return Math.min(CARDS_TO_DRAW, player.deck.length);
}

// Re-export as object for backwards compatibility
export const DrawPhaseHandler = {
  execute,
  canExecute,
  getDrawCount,
};
